// ** MACROS **

import convert from "convert";
import { v4 as uuidv4 } from 'uuid';
import { determineFuelCost } from "./UtilityFunctions";
//TODO: These constants to be moved into API Service Models and removed from here
const CO2_GRAMSPERGALLON = 8886.920832;
const CO2_GRAMSPERGALLON_AVOIDED = 8886.920832;
const CO2_GRAMSPERGALLONCNG = 6178.0470264;
const CO2_GRAMSPERGALLONDIESEL = 10180.125796;

const EMIT_VEHICLE_URL = "emissions-for-vehicle";
const EMIT_VEHICLE_LIST_URL = "emissions-for-vehicle-list";

export async function processEmissionsForVehicle(vehicle, calcByFuel, rawVehicleEmissionsFromService, rawBackcastedEmissionsFromService, user, emitApi) {

    // TODO: In my opinion, these values should actually be set to null if their 'generated' counterparts are null - that is, if they were never going to be calculated by virtue of the user's selected methodology. ie. pm10Avoided should be null if the selected model doesn't process particulate matter emissions.
    // This doesn't materially impact the product, it's purely a philosophical iritation. -NK
    vehicle.ghgAvoided = 0;
    vehicle.co2Avoided = 0;
    vehicle.coAvoided = 0;
    vehicle.ch4Avoided = 0;
    vehicle.noxAvoided = 0;
    vehicle.pm10Avoided = 0;
    vehicle.pm25Avoided = 0;
    // TODO: Reinstate bioCO2 when fully supported in emit-service - MH 
    vehicle.bioco2Avoided = null; // 0
    vehicle.fuelUnitsSaved = 0;
    vehicle.fuelCostSaved = 0;

    let emissionsResponse = null;
    // Only call the api on an empty list of raw emissions data from the service
    // Functionally this only supports the unit tests right now. The app always attempts to preprocess data -MH
    if (calcByFuel && rawVehicleEmissionsFromService.length < 1) emissionsResponse = await emissionsByFuel(vehicle, user, emitApi);
    // by mile calculation is supported by code and API but not avaialble through this interface
    if (!calcByFuel && rawVehicleEmissionsFromService.length < 1) emissionsResponse = await emissionsByMile(vehicle, user, emitApi);

    // Match the requested vehicle to the relevant response from the raw emissions (via the emit service) list -MH
    if (calcByFuel && rawVehicleEmissionsFromService.length > 0) {
        const a = rawVehicleEmissionsFromService.filter(v => v.vin === vehicle.vin);
        if (a.length > 0) emissionsResponse = a[0];
    }
    //return an empty vehicle if not in our list. included for error handling. shouldn't happen
    //TODO: handle error and communicate to user something unexpected has occured.
    if (!emissionsResponse) return vehicle;

    vehicle.ghgGenerated = emissionsResponse.co2Grams; //TODO Other models than MOVES3 complicate this -MH
    vehicle.co2Generated = emissionsResponse.co2Grams;
    vehicle.coGenerated = emissionsResponse.coGrams;
    vehicle.ch4Generated = emissionsResponse.ch4Grams;
    vehicle.noxGenerated = emissionsResponse.noxGrams;
    vehicle.pm10Generated = emissionsResponse.pm10Grams;
    vehicle.pm25Generated = emissionsResponse.pm25Grams;
    vehicle.co2GeneratedFromKwhGeneration = emissionsResponse.co2GramsKwhGeneration;

    vehicle.fuelEconKML = vehicle.km / vehicle.fu_liters_actual;
    if (isNaN(vehicle.fuelEconKML) || !isFinite(vehicle.fuelEconKML) || vehicle.is_bev) {
        let notApplicable = "N/A";
        vehicle.fuelEconKML = notApplicable;
        vehicle.fuelEconMPG = notApplicable;
        vehicle.fuelEconKMG = notApplicable;
    } else {
        // TODO: Think about whether this is the best way to do this. It complicates the conversion function because with this logic, within the convert function we don't actually convert anything when handling fuel econ, we just pick the correct value to provide out of these 3. We could convert it on the fly there, but that would require magic numbers (since the convert data function should not be recieving the distance and fuel usage values), which defeats the whole point of introduing the conversion library... -NK
        vehicle.fuelEconMPG = vehicle.miles / convert(vehicle.fu_liters_actual, "liters").to("gallons");
        vehicle.fuelEconKMG = vehicle.km / convert(vehicle.fu_liters_actual, "liters").to("gallons");
    }

    if (vehicle.is_bev) {
        let emissionsBackcast = {};
        const iceComp = { vin: `${vehicle.vin}-comp`, vehicle_class: vehicle.vehicle_class, year: vehicle.year, fuel_gallons: vehicle.fuel_gallons, miles: vehicle.miles, is_gasoline: true, emissions_model: vehicle.emissions_model };

        if (calcByFuel) emissionsBackcast = await emissionsByFuel(iceComp, user, emitApi);
        // By mile calculation is supported by code and API, but not available through this interface - MH
        else emissionsBackcast = await emissionsByMile(iceComp, user, emitApi);

        // Add the GHG from kWh generation to the tailpipe GHG generated for BEVs
        vehicle.ghgGenerated += emissionsResponse.co2GramsKwhGeneration;

        vehicle.ghgAvoided = Math.max(0, emissionsBackcast.co2Grams - emissionsResponse.co2GramsKwhGeneration); // Right now co2 and ghg are 1 to 1 -MH
        vehicle.co2Avoided = Math.max(0, emissionsBackcast.co2Grams);
        vehicle.coAvoided = Math.max(0, emissionsBackcast.coGrams);
        vehicle.ch4Avoided = Math.max(0, emissionsBackcast.ch4Grams);
        vehicle.noxAvoided = Math.max(0, emissionsBackcast.noxGrams);
        vehicle.pm10Avoided = Math.max(0, emissionsBackcast.pm10Grams);
        vehicle.pm25Avoided = Math.max(0, emissionsBackcast.pm25Grams);
        // NOTE: Setting PM avoided to null as they do not apply to BEVs under the current methodologies. This should be in the service-api's domain as brake and tire wear can contribute on a per mile basis.
        // TODO: Reinstate BioCO2 when fully supported in emit-service -MH 
        vehicle.bioco2Avoided = null; // Math.max(0, emissionsBackcast.bioco2Grams);  
        // TODO: Remove null assignment once brake and tire emissions are fully supported -LS 3/27/25
        vehicle.pm10Generated = null; // Currently nulled out so that the table displays a dash rather than a 0. This line can be removed once T&BW are implemented. 
        vehicle.pm25Generated = null; // Currently nulled out so that the table displays a dash rather than a 0. This line can be removed once T&BW are implemented. 

        // TODO: Fuel Cost Savings does not factor in cost of kWh generation and probably should -MH
        vehicle.fuelUnitsSaved = vehicle.fu_liters;
        // NOTE that the fuel cost is always saved in $/gal, so we have to convert to gallons to use it without a bunch of extra steps.
        vehicle.fuelCostSaved = (vehicle.backcasted_fuel_cost && vehicle.fu_liters) ? (vehicle.backcasted_fuel_cost * convert(vehicle.fu_liters, "liters").to("gallons")) : 0;
    }
    if (vehicle.is_phev) {
        let emissionsBackcast = {};
        const iceComp = { vin: `${vehicle.vin}-comp`, vehicle_class: vehicle.vehicle_class, year: vehicle.year, fuel_gallons: convert(vehicle.fu_liters, "liters").to("gallons"), miles: vehicle.miles, is_gasoline: true, emissions_model: vehicle.emissions_model }
        if (calcByFuel) emissionsBackcast = await emissionsByFuel(iceComp, user, emitApi);
        // By mile calculation is supported by code and API, but not available through this interface -MH
        else emissionsBackcast = await emissionsByMile(iceComp, user, emitApi);
        // Add the GHG from kWh generation to the tailpipe GHG generated for PHEVs
        vehicle.ghgGenerated += vehicle.co2GeneratedFromKwhGeneration;

        vehicle.ghgAvoided = Math.max(0, emissionsBackcast.co2Grams - emissionsResponse.co2Grams - emissionsResponse.co2GramsKwhGeneration);
        vehicle.co2Avoided = Math.max(0, emissionsBackcast.co2Grams - emissionsResponse.co2Grams - emissionsResponse.co2GramsKwhGeneration);
        vehicle.coAvoided = Math.max(0, emissionsBackcast.coGrams - emissionsResponse.coGrams);
        vehicle.ch4Avoided = Math.max(0, emissionsBackcast.ch4Grams - emissionsResponse.ch4Grams);
        vehicle.noxAvoided = Math.max(0, emissionsBackcast.noxGrams - emissionsResponse.noxGrams);
        vehicle.pm10Avoided = Math.max(0, emissionsBackcast.pm10Grams - emissionsResponse.pm10Grams);
        vehicle.pm25Avoided = Math.max(0, emissionsBackcast.pm25Grams - emissionsResponse.pm25Grams);
        // TODO: Fuel Cost Savings does not factor in cost of kWh generation and probably should -MH
        // NOTE: What if fu_liters_actual is actually 0? -LS
        vehicle.fuelUnitsSaved = vehicle.fu_liters - vehicle.fu_liters_actual;
        // NOTE that the fuel cost is always saved in $/gal, so we have to convert to gallons to use it without a bunch of extra steps.
        vehicle.fuelCostSaved = (vehicle.backcasted_fuel_cost && vehicle.fuelUnitsSaved) ? (vehicle.backcasted_fuel_cost * convert(vehicle.fuelUnitsSaved, "liters").to("gallons")) : 0;
    }
    if (vehicle.is_cng) {
        let emissionsBackcast = {};
        // Use gasoline for is_ld vehicles (or if is_ld is undefined, in case that flag is not set); diesel for cng otherwise - MH
        const is_gasoline = vehicle.is_ld || typeof vehicle.is_ld === 'undefined';
        const is_diesel = !is_gasoline;

        const iceComp = { vin: `${vehicle.vin}-comp`, vehicle_class: vehicle.vehicle_class, year: vehicle.year, fuel_gallons: vehicle.fuel_gallons, miles: vehicle.miles, is_gasoline: is_gasoline, is_diesel: is_diesel, emissions_model: vehicle.emissions_model }
        if (calcByFuel) emissionsBackcast = await emissionsByFuel(iceComp, user, emitApi)
        // By mile calculation is supported by code and API, but not available through this interface -MH
        else emissionsBackcast = await emissionsByMile(iceComp, user, emitApi);

        // NOTE: we're zeroing out any negative savings here. There are no avoided emissions in these cases and the extra generated emissions are accounted for in the generated numbers. Showing a negative avoided number might be percieved as a double counting of the emission factor -MH
        vehicle.ghgAvoided = Math.max(0, emissionsBackcast.co2Grams - emissionsResponse.co2Grams);
        vehicle.co2Avoided = Math.max(0, emissionsBackcast.co2Grams - emissionsResponse.co2Grams);
        vehicle.coAvoided = Math.max(0, emissionsBackcast.coGrams - emissionsResponse.coGrams);
        vehicle.ch4Avoided = Math.max(0, emissionsBackcast.ch4Grams - emissionsResponse.ch4Grams);
        vehicle.noxAvoided = Math.max(0, emissionsBackcast.noxGrams - emissionsResponse.noxGrams);
        vehicle.pm10Avoided = Math.max(0, emissionsBackcast.pm10Grams - emissionsResponse.pm10Grams);
        vehicle.pm25Avoided = Math.max(0, emissionsBackcast.pm25Grams - emissionsResponse.pm25Grams);

        // NOTE: For CNG we're still burning fuel. We could use a GGE/DGE ratio to calculate a "savings" but this is not a standard output for our current GHG models and somewhat frought as different models use different GGE/DGE assumptions -MH
        vehicle.fuelUnitsSaved = null;
        vehicle.fuelCostSaved = null;
    }

    // vehicle.emit = (((parseFloat(POUNDS2TONS(vehicle.ghgLbs)) + parseFloat(vehicle.pm25Lbs) + parseFloat(vehicle.pm10Lbs) + parseFloat(POUNDS2TONS(vehicle.coLbs))) / vehicle.miles) * 1000);
    return vehicle;
}

// Calculate fleet-level level emissions figures (limited by user's filters)
export function summarizeEmissions(emissionsDataByVehicle) {
    const summaryObj = {
        // Used in: Info cards
        ghgGenerated: 0,
        co2Generated: 0,
        coGenerated: 0,
        ch4Generated: 0,
        noxGenerated: 0,
        pm10Generated: 0,
        pm25Generated: 0,
        bioco2Generated: null,
        ghgAvoided: 0,
        co2Avoided: 0,
        coAvoided: 0,
        bioco2Avoided: null,
        ch4Avoided: 0,
        noxAvoided: 0,
        pm10Avoided: 0,
        pm25Avoided: 0,
        fuelCostSaved: 0,
        fuelUnitsSaved: 0,
        // Fleet composition distribution cards
        allVehicles: 0,
        gasolineVehicles: 0,
        dieselVehicles: 0,
        cngVehicles: 0,
        phevVehicles: 0,
        bevVehicles: 0,
        // Distance breakdown distribution cards
        totalDistance: 0,
        tradFuelDistance: 0,
        altFuelDistance: 0,
        gasolineDistance: 0,
        dieselDistance: 0,
        bevDistance: 0,
        phevDistance: 0,
        cngDistance: 0,
        // Emissions distribution card; Graph card (determinining whether drivetrains have data); 
        gasolineGhgGenerated: 0,
        dieselGhgGenerated: 0,
        bevGhgGenerated: 0,
        phevGhgGenerated: 0,
        cngGhgGenerated: 0,
        bevGhgAvoided: 0,
        phevGhgAvoided: 0,
        cngGhgAvoided: 0,
        // These values aren't used, but could be helpful in the future
        gasolineFuelUnits: 0,
        dieselFuelUnits: 0,
        phevFuelUnits: 0,
        cngFuelUnits: 0,
        totalFuelUnits: 0,
        bevFuelUnitsSaved: 0,
        phevFuelUnitsSaved: 0,
        phevKwh: 0,
    }
    emissionsDataByVehicle.forEach((vehicle) => {

        summaryObj.ghgGenerated += vehicle.ghgGenerated;
        summaryObj.co2Generated += vehicle.co2Generated;
        summaryObj.coGenerated += vehicle.coGenerated;
        summaryObj.ch4Generated += vehicle.ch4Generated;
        summaryObj.noxGenerated += vehicle.noxGenerated;
        summaryObj.pm10Generated += vehicle.pm10Generated;
        summaryObj.pm25Generated += vehicle.pm25Generated;
        summaryObj.totalDistance += vehicle.km;

        summaryObj.ghgAvoided += vehicle.ghgAvoided;
        summaryObj.co2Avoided += vehicle.co2Avoided;
        summaryObj.coAvoided += vehicle.coAvoided;
        summaryObj.ch4Avoided += vehicle.ch4Avoided;
        summaryObj.noxAvoided += vehicle.noxAvoided;
        summaryObj.pm10Avoided += vehicle.pm10Avoided;
        summaryObj.pm25Avoided += vehicle.pm25Avoided;

        if (vehicle.is_gasoline) {
            summaryObj.gasolineVehicles++;
            summaryObj.gasolineGhgGenerated += vehicle.ghgGenerated;
            summaryObj.gasolineFuelUnits += vehicle.fu_liters;
            summaryObj.tradFuelDistance += vehicle.km;
            summaryObj.gasolineDistance += vehicle.km;
        }
        if (vehicle.is_diesel) {
            summaryObj.dieselVehicles++;
            summaryObj.dieselGhgGenerated += vehicle.ghgGenerated;
            summaryObj.dieselFuelUnits += vehicle.fu_liters;
            summaryObj.tradFuelDistance += vehicle.km;
            summaryObj.dieselDistance += vehicle.km;
        }
        if (vehicle.is_phev) {
            summaryObj.phevVehicles++;
            // NOTE: It was previously decided that PHEVs should NOT contribute to the 'gasoline' category's fuel units & GHG generated. (See merge requests 59 & 143 for this repo) -NK
            summaryObj.phevGhgGenerated += vehicle.ghgGenerated;
            summaryObj.phevFuelUnits += vehicle.fu_liters_actual;
            summaryObj.phevKwh += vehicle.kwh;
            summaryObj.altFuelDistance += vehicle.km;
            summaryObj.phevDistance += vehicle.km;
            summaryObj.fuelCostSaved += vehicle.fuelCostSaved;
            summaryObj.fuelUnitsSaved += vehicle.fuelUnitsSaved;
            summaryObj.phevFuelUnitsSaved += vehicle.fuelUnitsSaved;
            summaryObj.phevGhgAvoided += vehicle.ghgAvoided;
        }
        if (vehicle.is_bev) {
            summaryObj.bevVehicles++;
            summaryObj.bevGhgGenerated += vehicle.ghgGenerated;
            summaryObj.altFuelDistance += vehicle.km;
            summaryObj.bevDistance += vehicle.km;
            summaryObj.fuelCostSaved += vehicle.fuelCostSaved;
            summaryObj.fuelUnitsSaved += vehicle.fuelUnitsSaved;
            summaryObj.bevFuelUnitsSaved += vehicle.fuelUnitsSaved;
            summaryObj.bevGhgAvoided += vehicle.ghgAvoided;
        }
        if (vehicle.is_cng) {
            summaryObj.cngVehicles++;
            summaryObj.cngGhgGenerated += vehicle.ghgGenerated;
            summaryObj.altFuelDistance += vehicle.km;
            summaryObj.cngDistance += vehicle.km;
            summaryObj.cngFuelUnits += vehicle.fu_liters;
            // NOTE: CNG Costs and Gallons Saved are more complex than EV values. CNG users don't necessarily measure fuel in gallons vs ccf and CNG costs are not necessarily set up in our system correctly. CNG Fuel Cost Savings are not currently applicable. -MH
            // summaryObj.fuelCostSaved += vehicle.fuelCostSaved;
            // summaryObj.fuelUnitsSaved += vehicle.fuelUnitsSaved;
            // summaryObj.cngFuelUnitsSaved += vehicle.fuelUnitsSaved;
            summaryObj.cngGhgAvoided += vehicle.ghgAvoided;
        }
    });

    summaryObj.totalFuelUnits = (summaryObj.gasolineFuelUnits + summaryObj.dieselFuelUnits + summaryObj.phevFuelUnits + summaryObj.cngFuelUnits);
    return summaryObj;
}

//TODO: properly the emit-service's job to run all of the below conversions - MH
export function co2GramsPerLiter(liters, cof) {
    cof = (cof && isFinite(cof) && !isNaN(cof)) ? cof : CO2_GRAMSPERGALLON;
    return (convert(liters, "liters").to("gallons") * cof);
}
export function co2GramsPerLiterCNG(liters, cof) {
    cof = (cof && isFinite(cof) && !isNaN(cof)) ? cof : CO2_GRAMSPERGALLONCNG;
    return (convert(liters, "liters").to("gallons") * cof);
}
export function co2GramsPerLiterDiesel(liters, cof) {
    cof = (cof && isFinite(cof) && !isNaN(cof)) ? cof : CO2_GRAMSPERGALLONDIESEL;
    return (convert(liters, "liters").to("gallons") * cof);
}
export function co2GramsPerLiterAvoided(liters, cof) {
    cof = (cof && isFinite(cof) && !isNaN(cof)) ? cof : CO2_GRAMSPERGALLON_AVOIDED;
    return (convert(liters, "liters").to("gallons") * cof);
}
export function kwhToGhgGrams(kwh, gramsGHGPerKwh, dbSettings) {
    gramsGHGPerKwh = (gramsGHGPerKwh && isFinite(gramsGHGPerKwh) && !isNaN(gramsGHGPerKwh)) ? gramsGHGPerKwh : dbSettings.ghg_kwh_gm;
    return (kwh * gramsGHGPerKwh);
}

export async function calculateEmissionsFromVehicleData(preprocessedVehicleList, calcByFuel, user, emitApi) {
    let rawVehicleEmissionsFromService = await getRawVehicleEmissionsFromService(preprocessedVehicleList, user, emitApi);
    // return(rawVehicleEmissionsFromService)
    let vehiclesWithProcessedEmissions = await Promise.all(
        preprocessedVehicleList.map((vehicle) => {
            return processEmissionsForVehicle(vehicle, calcByFuel, rawVehicleEmissionsFromService, null, user, emitApi);
        })
    );

    return vehiclesWithProcessedEmissions;
}

export function preprocessVehicleList(vehicleData, idleData, methodology, dbSettings) {
    let preprocessedVehicleList = vehicleData.map((vehicle) => {
        vehicle = ensureSingleDrivetrain(vehicle);
        // Add idle data to vehicle
        vehicle.idleHours = (idleData?.filter(id => { return id.vin === vehicle.vin }))?.[0]?.hours ?? 0;

        // Prepare vehicle to be run through emit service
        vehicle.emissions_model = methodology.value;
        // REQUIRED BY EMIT API (miles and fuel gallon terms)
        // Imperial units chosen for API interface for customer cohort reasons -MH
        vehicle.miles = vehicle.km ? convert(vehicle.km, "kilometers").to("miles") : 0;
        vehicle.fuel_gallons = vehicle.fu_liters ? convert(vehicle.fu_liters, "liters").to("gallons") : 0;
        // Use Sawatch actual estimates in the case of PHEV
        if (vehicle.is_phev){
            vehicle.fuel_gallons = vehicle.fu_liters_actual ? convert(vehicle.fu_liters_actual, "liters").to("gallons") : 0;
            vehicle.kwh = vehicle.charge_kwh ? vehicle.charge_kwh : vehicle.kwh;
        }
        // Default to gasoline - TODO remove this default and make explicit or reject vehicle - MH
        if (!vehicle.is_diesel && !vehicle.is_phev && !vehicle.is_bev && !vehicle.is_cng && !vehicle.is_propane) vehicle.is_gasoline = true;

        // Addition of uuid is to allow search matching in the event that vehicles don't have VINs
        // These will change on every render but searching is still stable
        // NOTE: In place to accommodate FX util DBs - LS 3/26/25
        vehicle.uuid = uuidv4();

        // Add in settings required for emissions processing
        vehicle.backcasted_fuel_cost = determineFuelCost(vehicle, dbSettings);
        vehicle.backcasted_kwh_cost = vehicle.backcasted_kwh_cost ?? dbSettings.local_kwh_cost;
        vehicle.backcasted_ghg_kwh_gm = vehicle.backcasted_ghg_kwh_gm ?? dbSettings.ghg_kwh_gm;
        vehicle.settings = { grams_ghg_per_kwh: vehicle.backcasted_ghg_kwh_gm } //bundle ghg intensity into a settings object as expected by emit-service TODO: could this be refactored?

        // Assign vehicle fuel type text value for display in table
        if (vehicle.is_gasoline) vehicle.drivetrain = 'Gasoline';
        if (vehicle.is_diesel) vehicle.drivetrain = 'Diesel';
        if (vehicle.is_bev) vehicle.drivetrain = 'BEV';
        if (vehicle.is_phev) vehicle.drivetrain = 'PHEV';
        if (vehicle.is_cng) vehicle.drivetrain = 'CNG';

        // Set VIN to user-defined VIN if available, if not, maintain existing VIN, if no existing vin, set to 'not provided'.
        // Set Asset ID to VIN if available, otherwise set it to a dash.
        // TODO: Clean up this logic... it works as expected but it isn't pretty.
        vehicle.vin = (vehicle.user_defined_vin && vehicle.user_defined_vin !== 'null') ? vehicle.user_defined_vin : vehicle.vin;
        if (vehicle.asset_id === 'null') vehicle.asset_id = null;
        if (vehicle.vin === 'null') vehicle.vin = null;
        if ((vehicle.asset_id === undefined || vehicle.asset_id === null || vehicle.asset_id === 'null' || vehicle.asset_id.toLowerCase() === 'none') && (vehicle.vin && vehicle.vin !== "VIN Decoding Error")) vehicle.asset_id = vehicle.vin;
        return vehicle;
    })

    return preprocessedVehicleList;
}

function ensureSingleDrivetrain(vehicle) {
    // TODO some code golf is warranted here. Doing it the dumb way to make sure it works -MH
    if (vehicle.is_gasoline) {
        vehicle.is_diesel = false;
        vehicle.is_cng = false;
        vehicle.is_bev = false;
        vehicle.is_phev = false;
    }
    if (vehicle.is_bev) {
        vehicle.is_cng = false;
        vehicle.is_diesel = false;
        vehicle.is_phev = false;
        vehicle.is_gasoline = false;
    }
    if (vehicle.is_diesel) {
        vehicle.is_cng = false;
        vehicle.is_bev = false;
        vehicle.is_phev = false;
        vehicle.is_gasoline = false;
    }
    if (vehicle.is_phev) {
        vehicle.is_cng = false;
        vehicle.is_diesel = false;
        vehicle.is_bev = false;
        vehicle.is_gasoline = false;
    }
    if (vehicle.is_cng) {
        vehicle.is_phev = false;
        vehicle.is_diesel = false;
        vehicle.is_bev = false;
        vehicle.is_gasoline = false;
    }

    return vehicle;
}

export function processGraphData(rawGraphData, summaryData, beginDate, endDate, dbSettings) {
    // TODO: Some code golfing would make this function way more readable. There's a lot of repetitive functionality that makes this function look really scary, even though it's actually relatively straightforward. -NK
    const graphData = {}

    const graphGasolineLiters = rawGraphData.reduce((sum, x) => sum + x.gasoline_liters, 0);
    const graphDieselLiters = rawGraphData.reduce((sum, x) => sum + x.diesel_liters, 0);
    const graphPhevLiters = rawGraphData.reduce((sum, x) => sum + x.phev_liters, 0);
    const graphBevLiters = rawGraphData.reduce((sum, x) => sum + x.bev_liter_reduction, 0);
    const graphCNGLiters = rawGraphData.reduce((sum, x) => sum + x.cng_liters, 0);

    const graphBevKWH = rawGraphData.reduce((sum, x) => sum + x.bev_kwh, 0);
    const graphPhevKWH = rawGraphData.reduce((sum, x) => sum + x.phev_kwh, 0);

    //TODO These values are likely to be NaN or Infinity for some fuel types with minimal or 0 travel yet. Those conditions are handled in the functions that consume cof values. Should write QA/QC Tests for these dumb cof patterns -MH
    const gasolineCof = summaryData.gasolineGhgGenerated / convert(graphGasolineLiters, "liters").to("gallons");
    const dieselCof = summaryData.dieselGhgGenerated / convert(graphDieselLiters, "liters").to("gallons");
    const phevCof = summaryData.phevGhgGenerated / convert(graphPhevLiters, "liters").to("gallons");
    const cngCof = summaryData.cngGhgGenerated / convert(graphCNGLiters, "liters").to("gallons");
    const bevCof = (summaryData.bevGhgGenerated + summaryData.bevGhgAvoided) / convert(graphBevLiters, "liters").to("gallons");

    const bevImpliedGHGPerKwh = summaryData.bevGhgGenerated / graphBevKWH;
    const phevImpliedGHGPerKwh = Math.min((summaryData.phevGhgGenerated - summaryData.phevGhgAvoided) / graphPhevKWH, 550); 
    //the magic number above prevents non-charging phevs from massively skewing the ghg/kwh figure
    //it is the largest reasonable GHG/kWh value in North America
    const cngImpliedGHGPerLiterSavings = (summaryData.cngGhgAvoided && summaryData.cngFuelUnits) ? (summaryData.cngGhgAvoided / summaryData.cngFuelUnits) : 0;

    graphData.bevGhgGenerated = rawGraphData.map((f) => { return (kwhToGhgGrams(f.bev_kwh, bevImpliedGHGPerKwh, dbSettings)) });
    graphData.gasolineGhgGenerated = rawGraphData.map((f) => { return (co2GramsPerLiter(f.gasoline_liters, gasolineCof)) })
    graphData.dieselGhgGenerated = rawGraphData.map((f) => { return (co2GramsPerLiterDiesel(f.diesel_liters, dieselCof)) })

    graphData.cngGhgGenerated = rawGraphData.map((f) => { return (co2GramsPerLiterCNG(f.cng_liters, cngCof)) })

    graphData.phevGhgGenerated = rawGraphData.map((f) => { return (co2GramsPerLiter(f.phev_liters, phevCof) + kwhToGhgGrams(f.phev_kwh, phevImpliedGHGPerKwh, dbSettings)) });
    graphData.phevGhgAvoided = rawGraphData.map((f) => { return (co2GramsPerLiterAvoided(f.phev_liter_reduction, phevCof) - kwhToGhgGrams(f.phev_kwh, phevImpliedGHGPerKwh, dbSettings)) });
    graphData.bevGhgAvoided = rawGraphData.map((f) => { return (co2GramsPerLiterAvoided(f.bev_liter_reduction, bevCof) - kwhToGhgGrams(f.bev_kwh, bevImpliedGHGPerKwh, dbSettings)) });
    graphData.cngGhgAvoided = rawGraphData.map((f) => { return (f.cng_liters * cngImpliedGHGPerLiterSavings) });
    graphData.totalGhgAvoided = graphData.bevGhgAvoided.map((f, idx) => { return f + graphData.phevGhgAvoided[idx] + graphData.cngGhgAvoided[idx] })

    graphData.ghgGenerated = [];
    const dailyGhgArray = [];
    for (let i = 0; i < graphData.gasolineGhgGenerated.length; i++) {
        graphData.ghgGenerated[i] = graphData.bevGhgGenerated[i] + graphData.gasolineGhgGenerated[i] + graphData.dieselGhgGenerated[i] + graphData.phevGhgGenerated[i] + graphData.cngGhgGenerated[i];
        dailyGhgArray.push(
            {
                localized_date: rawGraphData[i].localized_date,
                ghgGenerated: graphData.ghgGenerated[i],
                ghgAvoided: graphData.totalGhgAvoided[i],
                phevGhgGenerated: graphData.phevGhgGenerated[i],
                phevGhgAvoided: graphData.phevGhgAvoided[i],
                gasolineGhgGenerated: graphData.gasolineGhgGenerated[i],
                bevGhgGenerated: graphData.bevGhgGenerated[i],
                bevGhgAvoided: graphData.bevGhgAvoided[i],
                dieselGhgGenerated: graphData.dieselGhgGenerated[i],
                cngGhgGenerated: graphData.cngGhgGenerated[i],
                cngGhgAvoided: graphData.cngGhgAvoided[i]
            }
        );
    }
    const weeklyGhgGenerated = CompileIntoWeeks(dailyGhgArray, 'ghgGenerated', beginDate, endDate);
    const weeklyGhgAvoided = CompileIntoWeeks(dailyGhgArray, 'ghgAvoided', beginDate, endDate);
    const monthlyGhgGenerated = CompileIntoMonths(dailyGhgArray, 'ghgGenerated', beginDate, endDate);
    const monthlyGhgAvoided = CompileIntoMonths(dailyGhgArray, 'ghgAvoided', beginDate, endDate);
    //PHEV*****
    const weeklyPhevGhgGenerated = CompileIntoWeeks(dailyGhgArray, 'phevGhgGenerated', beginDate, endDate)
    const weeklyPhevGhgAvoided = CompileIntoWeeks(dailyGhgArray, 'phevGhgAvoided', beginDate, endDate)
    const monthlyPhevGhgGenerated = CompileIntoMonths(dailyGhgArray, 'phevGhgGenerated', beginDate, endDate)
    const monthlyPhevGhgAvoided = CompileIntoMonths(dailyGhgArray, 'phevGhgAvoided', beginDate, endDate)
    //BEV*********
    const weeklyBevGhgGenerated = CompileIntoWeeks(dailyGhgArray, 'bevGhgGenerated', beginDate, endDate)
    const weeklyBevGhgAvoided = CompileIntoWeeks(dailyGhgArray, 'bevGhgAvoided', beginDate, endDate)
    const monthlyBevGhgGenerated = CompileIntoMonths(dailyGhgArray, 'bevGhgGenerated', beginDate, endDate)
    const monthlyBevGhgAvoided = CompileIntoMonths(dailyGhgArray, 'bevGhgAvoided', beginDate, endDate)
    //GASOLINE******
    const weeklyGasolineGhgGenerated = CompileIntoWeeks(dailyGhgArray, 'gasolineGhgGenerated', beginDate, endDate)
    const monthlyGasolineGhgGenerated = CompileIntoMonths(dailyGhgArray, 'gasolineGhgGenerated', beginDate, endDate)
    //DIESEL********
    const weeklyDieselGhgGenerated = CompileIntoWeeks(dailyGhgArray, 'dieselGhgGenerated', beginDate, endDate)
    const monthlyDieselGhgGenerated = CompileIntoMonths(dailyGhgArray, 'dieselGhgGenerated', beginDate, endDate)
    //CNG********
    const weeklyCNGGhgGenerated = CompileIntoWeeks(dailyGhgArray, 'cngGhgGenerated', beginDate, endDate)
    const weeklyCNGGhgAvoided = CompileIntoWeeks(dailyGhgArray, 'cngGhgAvoided', beginDate, endDate)
    const monthlyCNGGhgGenerated = CompileIntoMonths(dailyGhgArray, 'cngGhgGenerated', beginDate, endDate)
    const monthlyCNGGhgAvoided = CompileIntoMonths(dailyGhgArray, 'cngGhgAvoided', beginDate, endDate)

    //GASOLINE*******
    graphData.weeklyGasolineGhgGenerated = weeklyGasolineGhgGenerated.map((g) => { return g.gasolineGhgGenerated })
    graphData.monthlyGasolineGhgGenerated = monthlyGasolineGhgGenerated.map((g) => { return g.gasolineGhgGenerated })
    //BEV*********
    graphData.weeklyBevGhgGenerated = weeklyBevGhgGenerated.map((g) => { return g.bevGhgGenerated })
    graphData.weeklyBevGhgAvoided = weeklyBevGhgAvoided.map((g) => { return g.bevGhgAvoided })
    graphData.monthlyBevGhgGenerated = monthlyBevGhgGenerated.map((g) => { return g.bevGhgGenerated })
    graphData.monthlyBevGhgAvoided = monthlyBevGhgAvoided.map((g) => { return g.bevGhgAvoided })
    //PHEV***********
    graphData.weeklyPhevGhgGenerated = weeklyPhevGhgGenerated.map((g) => { return g.phevGhgGenerated })
    graphData.weeklyPhevGhgAvoided = weeklyPhevGhgAvoided.map((g) => { return g.phevGhgAvoided })
    graphData.monthlyPhevGhgGenerated = monthlyPhevGhgGenerated.map((g) => { return g.phevGhgGenerated })
    graphData.monthlyPhevGhgAvoided = monthlyPhevGhgAvoided.map((g) => { return g.phevGhgAvoided })
    //Diesel*******
    graphData.weeklyDieselGhgGenerated = weeklyDieselGhgGenerated.map((g) => { return g.dieselGhgGenerated })
    graphData.monthlyDieselGhgGenerated = monthlyDieselGhgGenerated.map((g) => { return g.dieselGhgGenerated })
    //CNG*******
    graphData.weeklyCNGGhgGenerated = weeklyCNGGhgGenerated.map((g) => { return g.cngGhgGenerated })
    graphData.weeklyCNGGhgAvoided = weeklyCNGGhgAvoided.map((g) => { return g.cngGhgAvoided })
    graphData.monthlyCNGGhgGenerated = monthlyCNGGhgGenerated.map((g) => { return g.cngGhgGenerated })
    graphData.monthlyCNGGhgAvoided = monthlyCNGGhgAvoided.map((g) => { return g.cngGhgAvoided })

    graphData.weeklyGhgGenerated = weeklyGhgGenerated.map((g) => { return g.ghgGenerated });
    graphData.weeklyGhgAvoided = weeklyGhgAvoided.map((g) => { return g.ghgAvoided });

    graphData.monthlyGhgGenerated = monthlyGhgGenerated.map((g) => { return g.ghgGenerated });
    graphData.monthlyGhgAvoided = monthlyGhgAvoided.map((g) => { return g.ghgAvoided });

    graphData.weeklyStartDates = weeklyGhgGenerated.map((g) => { return g.sunday });
    graphData.monthlyStartDates = monthlyGhgGenerated.map((g) => { return g.moy });

    // Neither of these calculation results are used, and ratio is usually NaN if the day has a km value of 0 anyway... can we remove entirely? -NK
    // graphData.miles = rawGraphData.map((f) => { return (convert(f.km,"kilometers").to("miles")) });
    // graphData.ratio = rawGraphData.map((f) => { return (f.fu_liters_actual / f.km) });
    graphData.dailyLabels = rawGraphData.map((f) => {
        let s = f.localized_date.split('T');
        return s[0]
    });
    graphData.weeklyLabels = graphData.weeklyStartDates;
    graphData.monthlyLabels = graphData.monthlyStartDates;

    graphData.dailyGhgGeneratedCumulative = calculateTotalEmissions(graphData.ghgGenerated);
    graphData.weeklyGhgGeneratedCumulative = calculateTotalEmissions(graphData.weeklyGhgGenerated);
    graphData.monthlyGhgGeneratedCumulative = calculateTotalEmissions(graphData.monthlyGhgGenerated);

    graphData.weeklyPhevGhgGeneratedCumulative = calculateTotalEmissions(graphData.weeklyPhevGhgGenerated)
    graphData.weeklyPhevGhgAvoidedCumulative = calculateTotalEmissions(graphData.weeklyPhevGhgAvoided)
    graphData.monthlyPhevGhgGeneratedCumulative = calculateTotalEmissions(graphData.monthlyPhevGhgGenerated)
    graphData.monthlyPhevGhgAvoidedCumulative = calculateTotalEmissions(graphData.monthlyPhevGhgAvoided)

    graphData.weeklyBevGhgGeneratedCumulative = calculateTotalEmissions(graphData.weeklyBevGhgGenerated)
    graphData.weeklyBevGhgAvoidedCumulative = calculateTotalEmissions(graphData.weeklyBevGhgAvoided)
    graphData.monthlyBevGhgGeneratedCumulative = calculateTotalEmissions(graphData.monthlyBevGhgGenerated)
    graphData.monthlyBevGhgAvoidedCumulative = calculateTotalEmissions(graphData.monthlyBevGhgAvoided)

    graphData.weeklyGasolineGhgGeneratedCumulative = calculateTotalEmissions(graphData.weeklyGasolineGhgGenerated)
    graphData.monthlyGasolineGhgGeneratedCumulative = calculateTotalEmissions(graphData.monthlyGasolineGhgGenerated)

    graphData.weeklyCNGGhgGeneratedCumulative = calculateTotalEmissions(graphData.weeklyCNGGhgGenerated)
    graphData.weeklyCNGGhgAvoidedCumulative = calculateTotalEmissions(graphData.weeklyCNGGhgAvoided)
    graphData.monthlyCNGGhgGeneratedCumulative = calculateTotalEmissions(graphData.monthlyCNGGhgGenerated)
    graphData.monthlyCNGGhgAvoidedCumulative = calculateTotalEmissions(graphData.monthlyCNGGhgAvoided)

    graphData.weeklyDieselGhgGeneratedCumulative = calculateTotalEmissions(graphData.weeklyDieselGhgGenerated)
    graphData.monthlyDieselGhgGeneratedCumulative = calculateTotalEmissions(graphData.monthlyDieselGhgGenerated)

    graphData.dailyGhgAvoidedCumulative = calculateDailyReductions(graphData.phevGhgAvoided, graphData.bevGhgAvoided);
    graphData.weeklyGhgAvoidedCumulative = calculateDailyReductions(graphData.weeklyGhgAvoided);
    graphData.monthlyGhgAvoidedCumulative = calculateDailyReductions(graphData.monthlyGhgAvoided);

    //use the cumulative tie out function to make sure the terminal values on our cumulative
    //graphs always match our table values(despite rounding and bucketing differences)
    graphData.dailyGhgGeneratedCumulative = cumulativeTotalTieOut(graphData.dailyGhgGeneratedCumulative, summaryData.ghgGenerated);
    graphData.weeklyGhgGeneratedCumulative = cumulativeTotalTieOut(graphData.weeklyGhgGeneratedCumulative, summaryData.ghgGenerated);
    graphData.monthlyGhgGeneratedCumulative = cumulativeTotalTieOut(graphData.monthlyGhgGeneratedCumulative, summaryData.ghgGenerated);

    graphData.dailyGhgAvoidedCumulative = cumulativeTotalTieOut(graphData.dailyGhgAvoidedCumulative, summaryData.ghgAvoided);
    graphData.weeklyGhgAvoidedCumulative = cumulativeTotalTieOut(graphData.weeklyGhgAvoidedCumulative, summaryData.ghgAvoided);
    graphData.monthlyGhgAvoidedCumulative = cumulativeTotalTieOut(graphData.monthlyGhgAvoidedCumulative, summaryData.ghgAvoided);

    return graphData
}

function calculateTotalEmissions(data) {
    let total = 0;
    let totalArr = [];
    data.forEach((item) => {
        total += item;
        totalArr.push(total)
    })
    return totalArr;
}

function cumulativeTotalTieOut(array, tableTerminalValue) {
    const arrTermination = Math.max(...array);
    //if the table value is less than the graph value 
    if (tableTerminalValue <= arrTermination) {
        return array.map((a) => { return a <= tableTerminalValue ? a : tableTerminalValue });
    }
    //if the table term value is greater than the array, plus up array term
    return array.map((a) => {
        if (a <= tableTerminalValue && a < arrTermination) return a;
        if (a >= arrTermination) return tableTerminalValue;
        return a;
    });
}

function calculateDailyReductions(reductionsArray0, reductionsArray1) {
    let totalArray = [];
    let total = 0;
    for (let i = 0; i < reductionsArray0.length; i++) {
        let value = reductionsArray0[i];
        if (reductionsArray1) value = (reductionsArray0[i] + reductionsArray1[i]);
        total += value;
        totalArray.push(total);
    }
    return totalArray;
}

// TODO: we could hypothetically format these to use some flavor of SWR, but ran out of time in the current effort -NK 
//list of vehicles emissions call
export async function getRawVehicleEmissionsFromService(preprocessedVehicleList, user, emitApi) {
    const headers = { "Authorization": `Bearer ${user.token}`, "Content-Type": "application/json" };
    const requestBody = preprocessedVehicleList;
    const body = JSON.stringify({ data: requestBody });
    return await fetch(`${emitApi}${EMIT_VEHICLE_LIST_URL}`, {
        headers: headers, body: body, method: "POST"
    })
        .then((resp) => resp.json())
        .then((data) => { return data })
        .catch((err) => { console.error('error', err) });
}

//single vehicle emissions call
async function emissionsByFuel(vehicle, user, emitApi) {
    const headers = { "Authorization": `Bearer ${user.token}`, "Content-Type": "application/json" };
    const body = JSON.stringify(vehicle);
    return await fetch(`${emitApi}${EMIT_VEHICLE_URL}`, {
        headers: headers, body: body, method: "POST"
    })
        .then((resp) => resp.json())
        .then((data) => { return data })
        .catch((err) => { console.error('error', err) });
}
// by mile calculation is supported by code and API but not avaialble through this interface
async function emissionsByMile(vehicle, user, emitApi) {
    let headers = { "Authorization": `Bearer ${user.token}`, "Content-Type": "application/json" };
    const body = JSON.stringify(vehicle);
    return await fetch(`${emitApi}${EMIT_VEHICLE_URL}`, {
        headers: headers, body: body, method: "POST"
    })
        .then((resp) => resp.json())
        .then((data) => { return data })
        .catch((err) => { console.error('error', err) });
}

export function CompileIntoWeeks(events, key, beginDate, endDate) {
    // Helper function to get the ISO week number for a given date
    const getISOWeekNumber = function (date) {
        let utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = utcDate.getUTCDay() || 7;
        utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
        return Math.ceil((((utcDate - yearStart) / 86400000) + 1) / 7);
    };

    // Helper function to get the Sunday (start date) of the week for a given date string
    const getWeekStartDate = function (dateString) {
        let date = new Date(Date.parse(dateString));
        let sunday = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = sunday.getUTCDay() || 7;
        sunday.setUTCDate(sunday.getUTCDate() + 1 - dayNum);
        return sunday;
    };

    // Object to store week aggregations, keyed by the week's Sunday ISO date string
    const weeklyAggregations = {};

    // Iterate through each day in the date range to identify unique week start dates
    let currentDate = new Date(beginDate);
    while (currentDate <= new Date(endDate)) {
        let weekStartDate = getWeekStartDate(currentDate);
        let weekNumber = getISOWeekNumber(weekStartDate);
        let weekStartKey = weekStartDate.toISOString().split('T')[0]; // ISO date string as key

        if (!weeklyAggregations[weekStartKey]) {
            weeklyAggregations[weekStartKey] = {
                woy: weekNumber,
                sunday: weekStartDate,
                [key]: 0 // Initialize the specified key to 0
            };
        }
        currentDate.setDate(currentDate.getDate() + 1);
    }

    // Convert the weeklyAggregations object into an array for easier processing
    const weeklyAggregationArray = Object.values(weeklyAggregations);

    // Sort events by date for consistent aggregation
    const sortedEvents = events.sort(sortByParsedDateInt);

    // Aggregate event values into the corresponding week objects
    sortedEvents.forEach(event => {
        let weekStartDate = getWeekStartDate(event.localized_date);
        let weekStartKey = weekStartDate.toISOString().split('T')[0];
        if (weeklyAggregations[weekStartKey]) {
            weeklyAggregations[weekStartKey][key] += event[key];
        }
    });

    return weeklyAggregationArray;
}

export function CompileIntoMonths(events, key) {
    // Helper function to get the month and year in "MM/YYYY" format
    // Strings are have the format YYYY-MM-DD
    const getMonthYearString = (dateStr) => {
        const month = dateStr.slice(5,7)
        const year = parseInt(dateStr.slice(0,4));
        return `${month}/${year}`;
    };
    
    const uniqueMonthYears = [];
    events.forEach((e) => {
        if(uniqueMonthYears.indexOf(getMonthYearString(e.localized_date)) < 0)uniqueMonthYears.push(getMonthYearString(e.localized_date));
    })

    // Initialize an array to store month objects with aggregated values
    const monthlyAggregations = [];
    uniqueMonthYears.forEach(monthYear => {
        const month = {};
        month['moy'] = monthYear;
        month[key] = 0;
        monthlyAggregations.push(month);
    });
    // Sort events by date for consistent aggregation
    const sortedEvents = events.sort(sortByParsedDateInt);

    // Aggregate event values into the corresponding month objects
    sortedEvents.forEach(event => {
        const eventMonthYear = getMonthYearString(event.localized_date);
        monthlyAggregations.forEach(monthAggregation => {
            if (monthAggregation.moy === eventMonthYear) {
                monthAggregation[key] += event[key];
            }
        });
    });
    return monthlyAggregations;
}

function sortByParsedDateInt(a, b) {
    //these are strings not dates and look like 'YYYY-MM-DD' -MH
    const dateArrA = a.localized_date.replaceAll('-', '');
    const dateAsNumA = parseInt(dateArrA);
    const dateArrB = b.localized_date.replaceAll('-', '');
    const dateAsNumB = parseInt(dateArrB);
    //now converted to int like YYYYMMDD for consistent comparisons.
    if (dateAsNumA < dateAsNumB) return -1;
    if (dateAsNumB > dateAsNumA) return 1;
    return 0;
}